import * as React from 'react'
import { TextInput } from '../TextInput'
import { Popover } from 'utils/base'
import { AirportList } from '../List'
import { Container, DecorationContainer } from './Styles'
import { PlaceSelectList } from 'utils/types'
import * as api from 'utils/api'
import { convertPlacesToSelectList } from 'utils/convertPlaces'
import { ReactComponent as LongArrowLeft } from 'assets/images/LongArrowLeft.svg'
import { useMedia } from 'utils/hooks'
import { formBreakpoint } from 'globalStyles'

export const ACTIVE_SEARCH_OVERLAY_CLASS = 'active-search-overlay'

const addActiveSearchOverlay = () => document.body.classList.add(ACTIVE_SEARCH_OVERLAY_CLASS)
const removeActiveSearchOverlay = () => document.body.classList.remove(ACTIVE_SEARCH_OVERLAY_CLASS)

interface Props {
  label: string
  bluredToSubmit: React.RefObject<boolean>
  localStorageKey: string
  inputValue: PlaceSelectList | null
  handleSubmit: (value: PlaceSelectList | null) => void
}

type PropsWithAttrs = Props & React.InputHTMLAttributes<HTMLInputElement>

const InputDropdown = ({
  label,
  inputValue,
  bluredToSubmit,
  localStorageKey,
  handleSubmit,
  ...rest
}: PropsWithAttrs) => {
  const [places, setPlaces] = React.useState<PlaceSelectList[] | null>(null)
  const [value, setValue] = React.useState('')
  const [focused, setFocused] = React.useState(false)
  const [preventBlur, setPreventBlur] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null!)
  const keyNavigationRef = React.useRef<any>(null!)
  const timerIdRef = React.useRef<NodeJS.Timeout | null>(null)
  const isMobile = useMedia(`(max-width:${formBreakpoint})`)

  const handleResetPlaces = React.useCallback(() => setPlaces(null), [])

  const handleSearchPlaces = React.useCallback(
    (query: string) => {
      api
        .fetchAirports(query)
        .then((data) => {
          const modifiedPlaces =
            Array.isArray(data) && data.length ? convertPlacesToSelectList(data) : null
          setPlaces(modifiedPlaces)
        })
        .catch((_) => {
          handleResetPlaces()
        })
    },
    [handleResetPlaces]
  )

  const handleFocus = React.useCallback(() => {
    if (isMobile) addActiveSearchOverlay()
    inputRef.current.focus()
    setFocused(true)
  }, [isMobile])

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value)
      handleResetPlaces()
    },
    [handleResetPlaces]
  )

  const handleResetValue = React.useCallback(() => {
    if (!inputValue || inputValue.name !== value) {
      inputValue && handleSubmit(null)
      setValue('')

      bluredToSubmit.current && places && places.length && handleSubmit({ ...places[0] })
    }
  }, [inputValue, handleSubmit, value, places, bluredToSubmit])

  const saveInputBlur = React.useCallback(() => {
    setTimeout(() => inputRef.current.focus(), 0)
  }, [])

  const loseInputBlur = React.useCallback(() => {
    handleResetValue()
    setFocused(false)
    setTimeout(() => inputRef.current.blur(), 0)
    if (isMobile) removeActiveSearchOverlay()
  }, [handleResetValue, isMobile])

  const handleBlur = React.useCallback(() => {
    const blurStrategy = preventBlur ? saveInputBlur : loseInputBlur
    blurStrategy()
    setPreventBlur(false)
  }, [saveInputBlur, loseInputBlur, preventBlur])

  const handleInputKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    e.key in keyNavigationRef.current && keyNavigationRef.current[e.key](e)
  }, [])

  const handleClickItem = React.useCallback(
    (value: PlaceSelectList) => {
      loseInputBlur()
      handleSubmit({ ...value })
    },
    [handleSubmit, loseInputBlur]
  )

  const preventInputBlur = React.useCallback(() => setPreventBlur(true), [])

  React.useLayoutEffect(() => {
    const formValue = inputValue?.name ?? ''
    setValue(formValue)
    handleResetPlaces()
  }, [inputValue, handleResetPlaces])

  React.useEffect(() => {
    timerIdRef.current && clearTimeout(timerIdRef.current)
    if (!value.length) return
    timerIdRef.current = setTimeout(() => handleSearchPlaces(value), 100)

    return () => (timerIdRef.current ? clearTimeout(timerIdRef.current) : undefined)
  }, [value, handleSearchPlaces])

  const PreDecoration = (
    <DecorationContainer>
      {isMobile && focused ? (
        <span onClick={loseInputBlur}>
          <LongArrowLeft />
        </span>
      ) : null}
      <div onClick={handleFocus}>{label}</div>
    </DecorationContainer>
  )

  return (
    <Container>
      <TextInput
        {...rest}
        ref={inputRef}
        value={value}
        handleBlur={handleBlur}
        handleFocus={handleFocus}
        handleChange={handleChange}
        handleKeyDown={handleInputKeyDown}
        children={{ precomponent: PreDecoration }}
      />
      <Popover onMouseDown={preventInputBlur}>
        <AirportList
          show={focused}
          places={places}
          inputValue={value}
          localStorageKey={localStorageKey}
          navigationRef={keyNavigationRef}
          handleClick={handleClickItem}
        />
      </Popover>
    </Container>
  )
}

export default React.memo(InputDropdown)
